export type CredentialField = {
  key: string;
  label: string;
  type?: "text" | "password";
  placeholder?: string;
};

export type CatalogEntry = {
  id: string;
  name: string;
  description: string;
  icon: string;
  fields: CredentialField[];
  /** Shown in the connect dialog, before the fields. */
  setupSteps?: string[];
};

export const INTEGRATION_CATALOG: CatalogEntry[] = [
  {
    id: "salesforce",
    name: "Salesforce",
    description:
      "CRM — each call is logged as a Task on the caller's Contact or Lead (a Lead is created for new callers)",
    icon: "💼",
    setupSteps: [
      "In Salesforce Setup, open App Manager and create a new Connected App (or External Client App).",
      "Enable OAuth settings, add the scope \"Manage user data via APIs (api)\". Any callback URL works, e.g. https://m.chawowa.com.",
      "Tick \"Enable Client Credentials Flow\" and save.",
      "Under Manage → Edit Policies, set the Client Credentials \"Run As\" user to someone who can create Leads and Tasks.",
      "Copy the Consumer Key and Consumer Secret below. Instance URL is your My Domain address, e.g. https://yourcompany.my.salesforce.com.",
    ],
    fields: [
      {
        key: "client_id",
        label: "Client ID",
        placeholder: "Your Salesforce Connected App Client ID",
      },
      {
        key: "client_secret",
        label: "Client Secret",
        type: "password",
        placeholder: "Client Secret",
      },
      {
        key: "instance_url",
        label: "Instance URL",
        placeholder: "https://yourorg.salesforce.com",
      },
    ],
  },
  {
    id: "hubspot",
    name: "HubSpot",
    description: "Marketing & CRM — sync contacts and deals",
    icon: "📊",
    fields: [
      {
        key: "api_key",
        label: "API Key",
        type: "password",
        placeholder: "Your HubSpot Private App token",
      },
      {
        key: "portal_id",
        label: "Portal ID (optional)",
        placeholder: "Your HubSpot Portal ID",
      },
    ],
  },
  {
    id: "calcom",
    name: "Cal.com",
    description: "Scheduling — agents book appointments during calls",
    icon: "📅",
    fields: [
      {
        key: "api_key",
        label: "API Key",
        type: "password",
        placeholder: "Your Cal.com API key",
      },
      {
        key: "username",
        label: "Cal.com Username",
        placeholder: "Your Cal.com username (for availability checks)",
      },
      {
        key: "event_type_id",
        label: "Default Event Type ID",
        placeholder: "e.g. 5070431 — find in Cal.com Event Types URL",
      },
    ],
  },
  {
    id: "slack",
    name: "Slack",
    description: "Team messaging — get call notifications",
    icon: "💬",
    fields: [
      {
        key: "webhook_url",
        label: "Webhook URL",
        placeholder: "https://hooks.slack.com/services/...",
      },
      {
        key: "channel",
        label: "Channel (optional)",
        placeholder: "#call-notifications",
      },
    ],
  },
  {
    id: "custom",
    name: "Custom Webhook (CRM)",
    description:
      "Send each call's summary as JSON to any URL — works with Zapier, Make and n8n",
    icon: "🔗",
    setupSteps: [
      "Zapier: add a \"Webhooks by Zapier\" trigger → Catch Hook, and copy its URL.",
      "Make: add a \"Webhooks\" module → Custom webhook, and copy its address.",
      "n8n: add a Webhook node (POST), and copy its production URL.",
      "Paste the URL below, then use the test button to send a sample.",
    ],
    fields: [
      {
        key: "webhook_url",
        label: "Webhook URL",
        placeholder: "https://your-crm.example.com/webhooks/calls",
      },
      {
        key: "secret",
        label: "Shared secret (optional)",
        type: "password",
        placeholder: "Sent in X-Webhook-Secret header",
      },
    ],
  },
  {
    id: "odoo",
    name: "Odoo",
    description: "ERP — CRM leads, calendar events, invoicing",
    icon: "🏢",
    fields: [
      {
        key: "url",
        label: "Odoo URL",
        placeholder: "https://mycompany.odoo.com",
      },
      { key: "db", label: "Database Name", placeholder: "mycompany" },
      {
        key: "username",
        label: "Username / Email",
        placeholder: "admin@mycompany.com",
      },
      {
        key: "api_key",
        label: "API Key",
        type: "password",
        placeholder: "Your Odoo API key",
      },
    ],
  },
];
